import { createServerFn } from '@tanstack/react-start'
import { paginate } from './pagination'
import { renderToStaticMarkup } from 'react-dom/server'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeSlug from 'rehype-slug'
import rehypeAutolinkHeadings from 'rehype-autolink-headings'
import {
  getAllPosts,
  getAllTagCounts,
  getAllTags,
  getCategories,
  getCategory,
  getPost,
  getPostsByCategory,
  getPostsByTag,
  getSeriesPosts,
} from './posts'

import type { Components } from 'react-markdown'
import type { Post, PostSummary } from './posts'

function summarizePost(post: Post): PostSummary {
  return {
    slug: post.slug,
    category: post.category,
    frontmatter: post.frontmatter,
  }
}

const markdownComponents: Components = {
  h2: ({ children, node: _node, ...props }) => (
    <h2
      className="font-heading mt-10 mb-4 text-2xl leading-tight tracking-[-0.02em] text-ink sm:mt-12 sm:text-3xl"
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ children, node: _node, ...props }) => (
    <h3
      className="font-heading mt-8 mb-3 text-xl leading-tight tracking-[-0.02em] text-ink sm:mt-10 sm:text-2xl"
      {...props}
    >
      {children}
    </h3>
  ),
  p: ({ children, node: _node, ...props }) => (
    <p
      className="mb-5 text-base leading-7 text-ink-soft sm:leading-8"
      {...props}
    >
      {children}
    </p>
  ),
  ul: ({ children, node: _node, ...props }) => (
    <ul className="mb-5 space-y-2" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, node: _node, ...props }) => (
    <ol className="mb-5 list-inside list-decimal space-y-2" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, node: _node, ...props }) => (
    <li className="text-base leading-7 text-ink-soft sm:leading-8" {...props}>
      {children}
    </li>
  ),
  a: ({ children, href, node: _node, ...props }) => (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="font-semibold text-forest underline decoration-forest/30 underline-offset-2 transition-colors hover:decoration-forest"
      {...props}
    >
      {children}
    </a>
  ),
  pre: ({ children, node: _node, ...props }) => (
    <pre
      className="mb-6 overflow-x-auto rounded-2xl border border-line/80 bg-[#1a1a2e] p-4 text-sm leading-6 shadow-lg sm:p-6"
      {...props}
    >
      {children}
    </pre>
  ),
  blockquote: ({ children, node: _node, ...props }) => (
    <blockquote
      className="mb-6 border-l-4 border-forest pl-4 italic text-ink-soft sm:pl-6"
      {...props}
    >
      {children}
    </blockquote>
  ),
  hr: ({ node: _node, ...props }) => (
    <hr className="my-10 border-line" {...props} />
  ),
  img: ({ src, alt, node: _node, ...props }) => (
    <img
      src={src}
      alt={alt ?? ''}
      className="my-8 w-full rounded-2xl border border-line/60"
      loading="lazy"
      decoding="async"
      {...props}
    />
  ),
  table: ({ children, node: _node, ...props }) => (
    <div className="mb-6 overflow-x-auto rounded-2xl border border-line/60">
      <table className="w-full text-left text-sm sm:text-base" {...props}>
        {children}
      </table>
    </div>
  ),
  th: ({ children, node: _node, ...props }) => (
    <th
      className="border-b border-line bg-cream-100 px-4 py-3 font-bold text-ink"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, node: _node, ...props }) => (
    <td className="border-b border-line/60 px-4 py-3 text-ink-soft" {...props}>
      {children}
    </td>
  ),
}

type MarkdownTreeNode = {
  tagName?: string
  properties?: Record<string, unknown>
  children?: Array<MarkdownTreeNode>
}

function rehypeAccessibleTables() {
  return (tree: MarkdownTreeNode) => {
    function visit(node: MarkdownTreeNode, parent?: MarkdownTreeNode) {
      if (node.tagName === 'tr' && parent?.tagName === 'tbody') {
        const firstCell = node.children?.find(
          (child) => child.tagName === 'td' || child.tagName === 'th',
        )
        if (firstCell?.tagName === 'td') {
          firstCell.tagName = 'th'
          firstCell.properties = { ...firstCell.properties, scope: 'row' }
        }
      }

      node.children?.forEach((child) => visit(child, node))
    }

    visit(tree)
  }
}

function renderMarkdown(content: string) {
  return renderToStaticMarkup(
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[
        rehypeSlug,
        [rehypeAutolinkHeadings, { behavior: 'wrap' }],
        rehypeAccessibleTables,
        [rehypeHighlight, { ignoreMissing: true }],
      ]}
      components={markdownComponents}
    >
      {content}
    </ReactMarkdown>,
  )
}

export const getLatestPosts = createServerFn({ method: 'GET' }).handler(() =>
  getAllPosts()
    .slice(0, 3)
    .map((post) => ({
      category: post.category,
      slug: post.slug,
      title: post.frontmatter.title,
      date: post.frontmatter.date,
      series: post.frontmatter.series,
    })),
)

export const getBlogIndexData = createServerFn({ method: 'GET' })
  .validator((data: { tag?: string; category?: string; page?: number }) => data)
  .handler(({ data }) => {
    let posts = getAllPosts()
    if (data.category) {
      posts = posts.filter((post) => post.category === data.category)
    }
    if (data.tag) {
      posts = posts.filter((post) => post.frontmatter.tags.includes(data.tag!))
    }

    return {
      ...paginate(posts.map(summarizePost), data.page),
      categories: getCategories(),
      allTags: getAllTags(),
      totalPosts: getAllPosts().length,
    }
  })

export const getCategoryPageData = createServerFn({ method: 'GET' })
  .validator((data: { category: string; page?: number }) => data)
  .handler(({ data }) => ({
    category: getCategory(data.category),
    ...paginate(
      getPostsByCategory(data.category).map(summarizePost),
      data.page,
    ),
  }))

export const getPostPageData = createServerFn({ method: 'GET' })
  .validator((data: { category: string; slug: string }) => data)
  .handler(({ data }) => {
    const post = getPost(data.category, data.slug)
    const category = getCategory(data.category)
    if (!post || !category) return undefined

    const seriesPosts = (
      post.frontmatter.series ? getSeriesPosts(post.frontmatter.series) : []
    ).map((seriesPost) => ({
      slug: seriesPost.slug,
      category: seriesPost.category,
      title: seriesPost.frontmatter.title,
    }))

    const wordCount = post.content.trim().split(/\s+/).filter(Boolean).length
    const readingTime = Math.max(1, Math.ceil(wordCount / 200))
    const { content, ...postData } = post

    return {
      post: postData,
      contentHtml: renderMarkdown(content),
      wordCount,
      readingTime,
      category,
      seriesPosts,
      seriesIndex: seriesPosts.findIndex(
        (seriesPost) => seriesPost.slug === data.slug,
      ),
    }
  })

export const getTagsPageData = createServerFn({ method: 'GET' }).handler(
  () => ({ tagCounts: getAllTagCounts(), totalPosts: getAllPosts().length }),
)

export const getTagPageData = createServerFn({ method: 'GET' })
  .validator((data: { tag: string; page?: number }) => data)
  .handler(({ data }) => ({
    tag: data.tag,
    ...paginate(getPostsByTag(data.tag).map(summarizePost), data.page),
    allTags: getAllTags(),
    categories: getCategories(),
  }))
